/**
 * Generates a complete HTML page with report, fully self-contained with all styling
 * @returns The HTML content for the competitive intelligence report
 */
export const generateReportHTML = (): string => {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Competitive Intelligence Report</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    body {
      background-color: #0f0f1a;
      color: #fff;
      padding: 20px;
    }

    .report-container {
      max-width: 1600px;
      margin: 0 auto;
    }

    .grid-layout {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      grid-gap: 20px;
      width: 100%;
      max-width: 1600px;
      margin: 0 auto;
      position: relative;
    }

    .hero-card {
      grid-column: span 12;
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
      border-radius: 20px;
      padding: 40px;
      position: relative;
      overflow: hidden;
      box-shadow: 0 15px 30px rgba(0,0,0,0.4);
    }

    .hero-bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle at 10% 20%, rgba(255,65,108,0.2) 0%, transparent 60%),
                 radial-gradient(circle at 90% 80%, rgba(91,95,239,0.2) 0%, transparent 60%);
      opacity: 0.4;
      z-index: 0;
    }

    .hero-content {
      position: relative;
      z-index: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .hero-title {
      color: #fff;
      font-size: 3rem;
      margin: 0;
      font-weight: 800;
      text-shadow: 0 2px 10px rgba(0,0,0,0.3);
      background: linear-gradient(90deg, #fff, #a5b4fc);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .title-badge {
      display: flex;
      align-items: center;
      margin-top: 10px;
    }

    .title-badge p {
      color: rgba(255,255,255,0.9);
      margin: 0;
      font-size: 1.1rem;
      font-weight: 300;
    }

    .date-badge {
      background: #FF416C;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.9rem;
      margin-left: 15px;
      box-shadow: 0 5px 15px rgba(255,65,108,0.4);
    }

    .hero-subtitle {
      color: rgba(255,255,255,0.6);
      margin-top: 15px;
      max-width: 600px;
    }

    .hero-icon {
      width: 120px;
      height: 120px;
      background: rgba(255,255,255,0.05);
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      box-shadow: 0 10px 30px rgba(0,0,0,0.2);
    }

    .hero-icon span {
      font-size: 40px;
      color: white;
      transform: rotate(-10deg);
    }

    .card {
      background: rgba(30, 30, 60, 0.6);
      border-radius: 20px;
      padding: 25px;
      box-shadow: 0 10px 25px rgba(0,0,0,0.25);
      border: 1px solid rgba(255,255,255,0.1);
      backdrop-filter: blur(10px);
    }

    .card-wide {
      grid-column: span 8;
    }

    .card-medium {
      grid-column: span 4;
    }

    .card-full {
      grid-column: span 12;
    }

    .card-title {
      color: #fff;
      margin-top: 0;
      position: relative;
      font-size: 1.5rem;
    }

    .card-title span {
      position: relative;
      z-index: 1;
    }

    .card-title::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 30px;
      height: 3px;
      background: linear-gradient(90deg, #FF416C, transparent);
      border-radius: 3px;
    }

    .market-share-panel {
      position: relative;
      padding: 1rem;
      background: rgba(15, 15, 30, 0.4);
      border-radius: 12px;
      margin-top: 15px;
    }

    .stats-grid {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
    }

    .stat-box {
      text-align: center;
      padding: 15px;
      background: rgba(0,0,0,0.2);
      border-radius: 10px;
      flex: 1;
      margin-right: 10px;
    }

    .stat-box:last-child {
      margin-right: 0;
    }

    .stat-value {
      margin: 0;
      font-size: 2rem;
      font-weight: 700;
    }

    .stat-label {
      margin: 5px 0 0;
      color: rgba(255,255,255,0.7);
      font-size: 0.9rem;
    }

    .analysis-text {
      color: #ffffff;
      padding: 15px;
      background: rgba(0,0,0,0.2);
      border-radius: 10px;
    }

    .alert-corner {
      position: absolute;
      top: 0;
      right: 0;
      width: 50px;
      height: 50px;
      background: linear-gradient(135deg, transparent 30%, #FF416C 30%);
    }

    .alert-list {
      list-style-type: none;
      padding: 0;
      margin: 15px 0 0 0;
    }

    .alert-item {
      background: rgba(15, 15, 30, 0.4);
      border-radius: 10px;
      padding: 15px;
      margin-bottom: 10px;
    }

    .alert-item:last-child {
      margin-bottom: 0;
    }

    .alert-critical {
      border-left: 3px solid #FF416C;
    }

    .alert-high {
      border-left: 3px solid #2196F3;
    }

    .alert-medium {
      border-left: 3px solid #9C27B0;
    }

    .alert-title {
      font-weight: 600;
      color: #fff;
      margin-bottom: 5px;
    }

    .alert-content {
      color: rgba(255,255,255,0.7);
      font-size: 0.9rem;
    }

    .recommendations-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-gap: 20px;
      margin-top: 20px;
    }

    .recommendation-box {
      background: rgba(15, 15, 30, 0.4);
      border-radius: 12px;
      padding: 20px;
    }

    .short-term {
      border-top: 4px solid #4CAF50;
    }

    .medium-term {
      border-top: 4px solid #2196F3;
    }

    .long-term {
      border-top: 4px solid #9C27B0;
    }

    .recommendation-title {
      margin-top: 0;
    }

    .short-term .recommendation-title {
      color: #4CAF50;
    }

    .medium-term .recommendation-title {
      color: #2196F3;
    }

    .long-term .recommendation-title {
      color: #9C27B0;
    }

    .recommendation-list {
      color: rgba(255,255,255,0.8);
      padding-left: 20px;
      margin: 10px 0 0 0;
    }

    .recommendation-list li {
      margin-bottom: 10px;
    }

    .recommendation-list li:last-child {
      margin-bottom: 0;
    }

    @media (max-width: 1024px) {
      .recommendations-grid {
        grid-template-columns: 1fr;
        grid-gap: 15px;
      }

      .hero-title {
        font-size: 2.2rem;
      }

      .hero-icon {
        width: 80px;
        height: 80px;
      }

      .hero-icon span {
        font-size: 30px;
      }
    }

    @media (max-width: 768px) {
      .card-wide, .card-medium {
        grid-column: span 12;
      }

      .stats-grid {
        flex-direction: column;
      }

      .stat-box {
        margin-right: 0;
        margin-bottom: 10px;
      }

      .hero-content {
        flex-direction: column;
        align-items: flex-start;
      }

      .hero-icon {
        margin-top: 20px;
      }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="grid-layout">
      <!-- Hero Card -->
      <div class="hero-card">
        <div class="hero-bg"></div>
        <div class="hero-content">
          <div>
            <h1 class="hero-title">Competitive Intelligence</h1>
            <div class="title-badge">
              <p>Executive Report</p>
              <span class="date-badge">${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <p class="hero-subtitle">AI-powered analysis of market trends, competitor activity, and strategic recommendations for your business.</p>
          </div>
          <div class="hero-icon">
            <span>📊</span>
          </div>
        </div>
      </div>

      <!-- Market Share Analysis - Wider card -->
      <div class="card card-wide">
        <h2 class="card-title">
          <span>Market Share Analysis</span>
        </h2>
        <div class="market-share-panel">
          <div class="stats-grid">
            <div class="stat-box">
              <h3 class="stat-value" style="color: #4CAF50;">45%</h3>
              <p class="stat-label">Our Share</p>
            </div>
            <div class="stat-box">
              <h3 class="stat-value" style="color: #2196F3;">35%</h3>
              <p class="stat-label">Competitor A</p>
            </div>
            <div class="stat-box">
              <h3 class="stat-value" style="color: #FF9800;">25%</h3>
              <p class="stat-label">Competitor B</p>
            </div>
            <div class="stat-box">
              <h3 class="stat-value" style="color: #9C27B0;">18%</h3>
              <p class="stat-label">Competitor C</p>
            </div>
          </div>
          <p class="analysis-text">Our market share has increased by 5% over the last quarter, primarily due to the successful launch of our premium product line. Competitor A has maintained their position, while Competitor B has seen a slight decline in market share.</p>
        </div>
      </div>

      <!-- Competitor Alert Card -->
      <div class="card card-medium">
        <div class="alert-corner"></div>
        <h2 class="card-title">
          <span>Competitor Alerts</span>
        </h2>
        <ul class="alert-list">
          <li class="alert-item alert-critical">
            <div class="alert-title">New Product Launch</div>
            <div class="alert-content">Competitor A is launching a new product line next month targeting our core market segment.</div>
          </li>
          <li class="alert-item alert-high">
            <div class="alert-title">Price Reduction</div>
            <div class="alert-content">Competitor B has reduced prices across their entry-level products by 15%.</div>
          </li>
          <li class="alert-item alert-medium">
            <div class="alert-title">Market Expansion</div>
            <div class="alert-content">Competitor C is entering the European market with flagship stores in three major cities.</div>
          </li>
        </ul>
      </div>

      <!-- Strategic Recommendations Card -->
      <div class="card card-full">
        <h2 class="card-title">
          <span>Strategic Recommendations</span>
        </h2>
        <div class="recommendations-grid">
          <div class="recommendation-box short-term">
            <h3 class="recommendation-title">Short Term</h3>
            <ul class="recommendation-list">
              <li>Launch a targeted marketing campaign to counter Competitor A's upcoming product launch</li>
              <li>Implement a loyalty program for existing customers to reduce churn</li>
              <li>Develop bundle offers to maintain price competitiveness against Competitor B</li>
            </ul>
          </div>
          <div class="recommendation-box medium-term">
            <h3 class="recommendation-title">Medium Term</h3>
            <ul class="recommendation-list">
              <li>Accelerate development of next-generation product features</li>
              <li>Expand distribution channels in current markets</li>
              <li>Strengthen supply chain partnerships to improve cost efficiency</li>
            </ul>
          </div>
          <div class="recommendation-box long-term">
            <h3 class="recommendation-title">Long Term</h3>
            <ul class="recommendation-list">
              <li>Explore market entry opportunities in Southeast Asia</li>
              <li>Develop sustainability initiatives to differentiate from competitors</li>
              <li>Invest in AI and automation to reduce operational costs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
};

export const getCurrentReport = (): string => {
  return generateReportHTML();
};

export const markReportAsViewed = (): void => {
  // No-op function since we don't need to track viewed status anymore
  return;
};

export const isTodayReportViewed = (): boolean => {
  // Always return false to ensure the report icon doesn't show as viewed
  return false;
};
